import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, Check, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STAMP_PATTERNS, STAMP_COLORS, STAMP_COLOR_FAMILIES } from "@/lib/constants";

function ColorSwatch({ color, active, onSelect, size = "sm" }) {
  const dims = size === "lg" ? "h-12 w-12 sm:h-14 sm:w-14" : size === "md" ? "h-10 w-10" : "h-8 w-8";
  return (
    <button
      onClick={() => onSelect(color)}
      title={`${color.name} · ${color.code}`}
      aria-label={`${color.name} ${color.code}`}
      data-testid={`studio-color-${color.slug}`}
      className={`relative ${dims} rounded-sm shrink-0 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg focus:outline-none ${active ? "ring-2 ring-offset-2 ring-offset-background ring-primary scale-105 z-10" : "ring-1 ring-black/10"}`}
      style={{ backgroundColor: color.hex }}
    >
      {active && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Check className="h-4 w-4 text-white drop-shadow" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

function GroupedSwatches({ current, onSelect, size = "sm", query = "" }) {
  const q = query.trim().toLowerCase();
  const match = (c) => !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q) || c.family.toLowerCase().includes(q);
  const families = STAMP_COLOR_FAMILIES.map((fam) => ({ fam, colors: STAMP_COLORS.filter((c) => c.family === fam && match(c)) })).filter((g) => g.colors.length);

  if (!families.length) {
    return <p className="text-sm text-muted-foreground py-6" data-testid="studio-color-empty">No colors match "{query}". Try "gray", "clay", or a code like "MC5006".</p>;
  }
  return (
    <div className="space-y-5">
      {families.map(({ fam, colors }) => (
        <div key={fam}>
          <div className="text-[11px] font-bold tracking-wider text-muted-foreground mb-2.5">{fam.toUpperCase()}</div>
          <div className="flex flex-wrap gap-2.5">
            {colors.map((c) => (
              <ColorSwatch key={c.slug} color={c} active={c.slug === current.slug} onSelect={onSelect} size={size} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PatternTile({ pattern, color, onOpen }) {
  return (
    <button
      onClick={() => onOpen(pattern)}
      data-testid={`studio-pattern-${pattern.slug}`}
      className="group relative aspect-square overflow-hidden bg-muted ring-1 ring-black/10 focus:outline-none"
    >
      <img src={pattern.image} alt={`${pattern.name} stamped concrete`} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
      <span className="absolute inset-0 transition-colors duration-500" style={{ backgroundColor: color.hex, mixBlendMode: "multiply" }} />
      <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      <span className="absolute inset-x-0 bottom-0 p-4 text-left">
        <span className="block font-heading font-bold text-white text-sm sm:text-base tracking-tight">{pattern.name}</span>
        <span className="block text-white/70 text-[11px] mt-0.5">Tap to preview</span>
      </span>
    </button>
  );
}

export default function PatternStudio({ compact = false, color: colorProp, onColorChange }) {
  const [internalColor, setInternalColor] = useState(STAMP_COLORS[0]);
  const color = colorProp ?? internalColor;
  const setColor = onColorChange ?? setInternalColor;
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const patterns = compact ? STAMP_PATTERNS.slice(0, 3) : STAMP_PATTERNS;
  const popular = STAMP_COLORS.filter((c) => c.popular);

  return (
    <div data-testid="pattern-studio">
      {/* Color selector */}
      <div className="mb-8">
        <div className="flex items-baseline justify-between gap-4 mb-4">
          <span className="text-xs font-bold tracking-widest text-muted-foreground">STEP 1 — PICK A MASTERCOLOR®</span>
          <span className="text-sm font-bold text-primary" data-testid="studio-active-color">{color.name} · {color.code}</span>
        </div>
        {compact ? (
          <div className="flex flex-wrap gap-2.5">
            {popular.map((c) => (
              <ColorSwatch key={c.slug} color={c} active={c.slug === color.slug} onSelect={setColor} size="md" />
            ))}
          </div>
        ) : (
          <>
            <div className="relative mb-4 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search colors — e.g. gray, clay, MC5006"
                className="w-full h-10 pl-9 pr-8 rounded-sm border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                data-testid="studio-color-search"
              />
              {query && (
                <button onClick={() => setQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="studio-color-search-clear" aria-label="Clear search">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="max-h-[440px] overflow-y-auto pr-1">
              <GroupedSwatches current={color} onSelect={setColor} size="sm" query={query} />
            </div>
          </>
        )}
      </div>

      {/* Pattern grid */}
      <div className="mb-3">
        <span className="text-xs font-bold tracking-widest text-muted-foreground">STEP 2 — SEE IT ON EVERY PATTERN</span>
      </div>
      <div className={`grid gap-3 sm:gap-4 ${compact ? "grid-cols-3" : "grid-cols-2 md:grid-cols-3"}`}>
        {patterns.map((p) => (
          <PatternTile key={p.slug} pattern={p} color={color} onOpen={setActive} />
        ))}
      </div>

      {/* Detail modal / lightbox */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 flex flex-col p-4 sm:p-6"
            onClick={() => setActive(null)}
            data-testid="studio-modal"
          >
            <div className="flex items-center justify-between text-white shrink-0">
              <span className="text-sm font-bold tracking-wider text-white/70">STAMP STUDIO</span>
              <button onClick={() => setActive(null)} className="text-white/80 hover:text-white p-2" data-testid="studio-modal-close"><X className="h-6 w-6" /></button>
            </div>

            <div className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-8 min-h-0 py-4" onClick={(e) => e.stopPropagation()}>
              <motion.div
                key={active.slug}
                initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
                className="relative w-full max-w-[520px] aspect-square overflow-hidden ring-1 ring-white/15 shadow-2xl"
              >
                <img src={active.image} alt={active.name} className="absolute inset-0 w-full h-full object-cover" data-testid="studio-modal-image" />
                <span className="absolute inset-0 transition-colors duration-500" style={{ backgroundColor: color.hex, mixBlendMode: "multiply" }} />
              </motion.div>

              <div className="w-full max-w-sm text-white shrink-0">
                <h3 className="font-heading text-3xl font-extrabold">{active.name}</h3>
                <p className="text-white/70 mt-2 leading-relaxed text-sm">{active.desc}</p>

                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="h-6 w-6 rounded-sm ring-1 ring-white/20" style={{ backgroundColor: color.hex }} />
                    <span className="font-bold text-sm">{color.name}</span>
                    <span className="text-xs text-white/50">{color.code} / {color.family}</span>
                  </div>
                  <div className="max-h-[180px] overflow-y-auto pr-1 rounded-sm">
                    <div className="flex flex-wrap gap-2">
                      {STAMP_COLORS.map((c) => (
                        <ColorSwatch key={c.slug} color={c} active={c.slug === color.slug} onSelect={setColor} size="sm" />
                      ))}
                    </div>
                  </div>
                </div>

                <Button asChild className="mt-6 w-full bg-primary hover:bg-primary/90 rounded-sm font-bold h-12" data-testid="studio-modal-quote">
                  <Link to={`/contact?service=${encodeURIComponent("Stamped Concrete")}&ref=${encodeURIComponent(`${active.name} · ${color.name} (${color.code})`)}`}>
                    Get This Look Quoted <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <p className="text-center text-white/40 text-xs mt-3">MasterColor® shades are digital approximations. Ask us for physical samples.</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
